import React from 'react'
import { useLocation, Link as RouterLink } from 'react-router-dom'

import { toTitleCase } from '../../../utils/strings'
import { isAncestorPath } from '../../../utils/referrerPath'

import {
  Breadcrumbs as MuiBreadcrumbs,
  BreadcrumbsProps as MuiBreadcrumbsProps,
  Link,
  Typography,
  styled,
  useTheme,
} from '@mui/material'

const StyledMuiBreadcrumbs = styled(MuiBreadcrumbs)<MuiBreadcrumbsProps>(({ theme }) => ({}))

// Mirrors the (untyped-here, deliberately minimal) shape `useReferrerState` writes to
// router state on a reference-link click-through — see
// apps/platform/src/hooks/useReferrerState.ts. Declared locally rather than imported
// from the platform app: packages/ui is the lower-level package, so the dependency only
// ever points platform → ui, never back.
type ReferrerState = {
  backTo?: string
  backLabel?: string
}

/**
 * Path segments whose display label cannot be derived from the slug.
 *
 * `toTitleCase` upper-cases each word boundary, which is right for almost every route
 * and wrong for any slug that lost punctuation on its way into a URL: `whats-new`
 * comes back "Whats-New" — wrong capitalisation, a hyphen where a space belongs, and a
 * missing apostrophe — printed as the parent crumb on every announcement detail page.
 *
 * A map rather than a smarter `toTitleCase`, because there is nothing to be clever
 * about: no rule recovers an apostrophe from a URL slug. The same problem is already
 * handled ad hoc in `AnnouncementCard`, which overrides `useReferrerState`'s derived
 * label for this exact segment — see the comment there.
 *
 * Keep this SMALL. It is for slugs that title-case wrongly, not a general renaming
 * layer; a crumb that merely wants different words belongs in the caller's
 * `breadcrumbsOveride` (the last crumb) or the app's `pathLabels` (a parent crumb,
 * keyed by its full path so it cannot leak onto another route's segment).
 */
const SEGMENT_LABELS: Record<string, string> = {
  'whats-new': "What's new",
  // VRG-679 — an acronym; title-casing the slug gives "Gtm".
  gtm: 'GTM',
}

export interface BreadcrumbsProps {
  breadcrumbsOveride?: React.ReactNode
  /**
   * Display names keyed by a crumb's bare path (e.g. `/internal/content`), for pages whose
   * name is not their URL segment. Wins over `SEGMENT_LABELS`, which is keyed by the
   * segment alone and so would rename that segment under EVERY parent. Supplied by the
   * app (platform: `Header` passes `PAGE_LABELS`), because this package must not import
   * the app's routes. The LAST crumb still comes from `breadcrumbsOveride` when given.
   */
  pathLabels?: Readonly<Record<string, string>>
  certificateProduct?: {
    name: string
    linkToIndex?: boolean
  }
}

export const Breadcrumbs = (props: BreadcrumbsProps) => {
  const { breadcrumbsOveride, certificateProduct, pathLabels } = props
  const theme = useTheme()

  const location = useLocation()
  let pathnames = location.pathname.split('/').filter(x => x)

  // Present only on a click-through from a reference link (useReferrerState); undefined
  // on a direct visit, where every segment falls back to its bare route below.
  const referrer = location.state as ReferrerState | null

  // Special handling for certificate pages
  const isCertificatePage = pathnames[0] === 'certificates' && pathnames[1] && pathnames[1] !== 'create'
  if (isCertificatePage && certificateProduct) {
    // For certificate pages, show only "certificates" and product name, ignore sub-paths like /provenance, /scans, etc.
    pathnames = ['certificates', certificateProduct.name]
  }

  return (
    <StyledMuiBreadcrumbs aria-label="Breadcrumb" sx={{ fontSize: 14, '& .MuiBreadcrumbs-separator': { fontSize: 16} }}>
      {pathnames.map((value, index) => {
        // Route-grouping prefixes with no index page of their own — omitted from the trail
        // because they would render as dead links. The `to` paths below still include them via
        // `pathnames`, so sibling crumbs (e.g. "Companies" → /internal/companies) stay correct.
        //
        //   internal   the staff-area prefix
        //   reference  reserved out of the docs' guide categories so /docs/reference/glossary can
        //              live under /docs without a guide ever colliding with it. There is
        //              deliberately no /docs/reference page, so the crumb would 404 — the glossary
        //              trail reads "Docs / Glossary".
        if (value === 'internal' || value === 'reference') return null

        const last = index === pathnames.length - 1
        let to = `/${pathnames.slice(0, index + 1).join('/')}`

        // The segment's display text: the explicit label when the slug does not
        // title-case cleanly, otherwise the derived one. Resolved once here so the
        // three render branches below cannot disagree about what a segment is called.
        // Looked up on the BARE path, before `to` may be swapped for the referrer's
        // `backTo` (which carries a query string) below.
        const label = pathLabels?.[to] ?? SEGMENT_LABELS[value] ?? toTitleCase(value)

        // Special handling for certificate product name - link back to base certificate page
        const isCertificateProductName = isCertificatePage && certificateProduct &&
          index === 1 && value === certificateProduct.name
        if (isCertificateProductName && certificateProduct.linkToIndex !== false) {
          // Get the certificate ID from the original URL to link to the base certificate page
          const originalPathnames = location.pathname.split('/').filter(x => x)
          const certificateId = originalPathnames[1]
          to = `/certificates/${certificateId}`
        } else if (
          referrer?.backTo &&
          referrer.backTo.split('?')[0] === to &&
          isAncestorPath(referrer.backTo, location.pathname)
        ) {
          // This segment's bare route is exactly the list page the user drilled in
          // from — swap it for `backTo` (pathname + search) so clicking it restores
          // the list's filters/sort/page instead of a reset table. `isAncestorPath`
          // guards this to the hierarchical drill-in case (the same predicate
          // ReferrerBackLink uses), so a cross-surface referrer never bleeds into an
          // unrelated breadcrumb segment. See working-conventions §42.
          to = referrer.backTo
        }

        if (!last) {
          if (index == 0 && breadcrumbsOveride) {
            return (
              <Link color="inherit" component={RouterLink} to={to} key={to} sx={{ fontSize: 16}}>
                {label}
              </Link>
            )
          } else {
            return (
              <Link color="inherit" component={RouterLink} to={to} key={to} sx={{ fontSize: 16}}>
                {isCertificateProductName ? value : label}
              </Link>
            )
          }
        }

        if (last) {
          return (
            <Typography color="textPrimary" key={to} sx={{ fontSize: 16}}>
              {/* An explicit override is rendered verbatim (the caller already
                  chose the exact casing — e.g. an invoice id "6HR2ZE8S-0002" or
                  "Plan"); only path-derived segments get title-cased. */}
              {isCertificateProductName
                ? value
                : breadcrumbsOveride
                ? breadcrumbsOveride
                : label}
            </Typography>
          )
        }
      })}
    </StyledMuiBreadcrumbs>
  )
}
